import React, { useContext } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { PureModal, TableButton } from "components/ui";
import IoMdRefresh from "react-icons/lib/io/refresh";
import IoMdClipboard from "react-icons/lib/io/clipboard";
import { setOrderBatchIds } from "actions/batch";
import BulkOrderUpdate from "components/bulk-order-update";
import { createTab } from "actions/system-tray";
import {
  exportOrdersInvoiceReport,
  exportOrdersReport
} from "actions/orders-table";
import MdPrint from "react-icons/lib/md/print";
import FaFileExcelO from "react-icons/lib/fa/file-excel-o";
import IoIosPaper from "react-icons/lib/io/ios-paper";
import IoMdEdit from "react-icons/lib/io/edit";
import { browserHistory } from "browser-history";
import HasRolePermission from "components/shared/has-role-permission";
import { TableSettings } from "hooks/useTableSettings";
import { OrdersTableSettingsContext } from "./use-table";

const OrdersStatusTableControls = ({
  refreshData,
  isFetching,
  selectedRows,
  clearSelectedRows,
  query,
  queryDispatcher
}) => {
  const dispatch = useDispatch();
  const {
    tableSettings,
    updateTableSettings,
    tableColumns,
    resetTableSettings
  } = useContext(OrdersTableSettingsContext);
  return (
    <div className="query-table-controls">
      <TableButton
        onClick={() => {
          refreshData();
        }}
        disabled={isFetching}
      >
        <IoMdRefresh />
      </TableButton>
      <HasRolePermission
        allowedRoles={[
          "Administrator",
          "ServiceAdmin",
          "ServiceCoach",
          "OfficeAdministrator",
          "CompanyAdministrator",
          "ContractorAdministrator"
        ]}
      >
        <TableButton
          title="Export orders to excel file"
          onClick={() => {
            let orderColumnsToExport = [];
            for (const tableColumn of tableColumns) {
              let isColumnActive = true;
              if (tableSettings.hasOwnProperty(tableColumn.field)) {
                isColumnActive =
                  tableSettings[tableColumn.field].active || false;
              }
              orderColumnsToExport.push({
                column: tableColumn.field,
                active: isColumnActive
              });
            }
            dispatch(exportOrdersReport(query, orderColumnsToExport));
          }}
        >
          <FaFileExcelO />
        </TableButton>
      </HasRolePermission>
      <TableButton
        onClick={() => {
          dispatch(exportOrdersInvoiceReport(query));
        }}
        title="Export Invoice Report(all orders)."
      >
        <IoIosPaper />
      </TableButton>
      <HasRolePermission
        allowedRoles={[
          "Administrator",
          "ServiceAdmin",
          "ServiceCoach",
          "OfficeAdministrator",
          "CompanyAdministrator",
          "ContractorAdministrator"
        ]}
      >
        <TableButton
          title={`Print orders (${selectedRows.length} selected)`}
          disabled={selectedRows.length === 0}
          onClick={() => {
            browserHistory.push({
              pathname: "/orders/batch-print-orders",
              state: { orderIds: selectedRows.map(x => x.id) }
            });
          }}
        >
          <MdPrint />
        </TableButton>
      </HasRolePermission>
      <TableButton
        title={`Process orders (${selectedRows.length} selected)`}
        disabled={selectedRows.length === 0}
        onClick={() => {
          const orderIds = selectedRows.map(x => x.id);
          const orderTitles = selectedRows.map(x => x.invoice_number);
          dispatch(setOrderBatchIds(orderIds, orderTitles));
          dispatch(
            createTab({
              type: "orders",
              key: "orders",
              label: `Orders (${orderIds.length})`,
              meta: { ids: orderIds }
            })
          );
          clearSelectedRows();
        }}
      >
        <IoMdClipboard size={24} />
      </TableButton>
      <PureModal
        renderTrigger={({ openModal }) => (
          <TableButton
            disabled={selectedRows.length === 0}
            onClick={openModal}
            title={`Bulk update orders (${selectedRows.length} selected)`}
          >
            <IoMdEdit />
          </TableButton>
        )}
        renderContent={({ closeModal }) => (
          <BulkOrderUpdate
            orderIds={selectedRows.map(x => x.id)}
            onUpdateError={() => {
              clearSelectedRows();
            }}
            onUpdateSuccess={() => {
              closeModal();
              clearSelectedRows();
              refreshData();
            }}
          />
        )}
      />
      <div
        style={{
          display: "flex",
          flex: 1,
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        {selectedRows.length > 0 && (
          <span>Selected {selectedRows.length} Orders</span>
        )}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px"
        }}
      >
        <label
          style={{
            color: "rgba(255, 255, 255, 0.75)",
            fontSize: 14,
            fontWeight: 500
          }}
        >
          Product Line
        </label>
        <select
          style={{
            boxShadow: "inset 0px 0px 5px 1px rgb(126 126 126 / 56%)",
            fontSize: "9px",
            height: "20px",
            width: "120px",
            padding: "2px 16px",
            backgroundColor: "white",
            border: "0px solid #28b0a4",
            borderRadius: "3px",
            margin: 0
          }}
          value={query.filters.product_line?.[0] || ""}
          onChange={({ target: { value } }) => {
            queryDispatcher({
              type: "setFilter",
              by: "product_line",
              value: value ? [value] : []
            });
          }}
        >
          <option value="">All</option>
          <option value="CPAP">CPAP</option>
          <option value="CGM">CGM</option>
        </select>
        <select
          style={{
            boxShadow: "inset 0px 0px 5px 1px rgb(126 126 126 / 56%)",
            fontSize: "9px",
            height: "20px",
            width: "120px",
            padding: "2px 16px",
            backgroundColor: "white",
            border: "0px solid #28b0a4",
            borderRadius: "3px",
            margin: 0
          }}
          value={query.filters?.order}
          onChange={({ target }) => {
            queryDispatcher({
              type: "setFilter",
              by: "order",
              value: target.value
            });
          }}
        >
          <option value="all">All Orders</option>
          <option value="mine">My Orders</option>
        </select>
        <div className="action-buttons companies-table-action-buttons">
          <TableSettings
            columns={tableColumns}
            tableSettings={tableSettings}
            updateTableSettings={updateTableSettings}
            resetTableSettings={resetTableSettings}
          />
        </div>
      </div>
    </div>
  );
};

export default OrdersStatusTableControls;

OrdersStatusTableControls.propTypes = {
  refreshData: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  selectedRows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string
    })
  ).isRequired,
  clearSelectedRows: PropTypes.func.isRequired,
  query: PropTypes.object.isRequired,
  queryDispatcher: PropTypes.func.isRequired
};
